// src/pages/Register.js
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { setAuth } from '../utils/auth';
import { useNotification } from '../hooks/useNotification';
import NotificationContainer from '../components/NotificationContainer';
import { analyticsEvents } from '../utils/analyticsEvents';

const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [resendCooldown, setResendCooldown] = useState(30); // 30 second cooldown
  const [resendLoading, setResendLoading] = useState(false);
  const navigate = useNavigate();
  const { notifications, showSuccess, showError, removeNotification } = useNotification();

  // Password requirements validation
  const passwordRequirements = {
    minLength: password.length >= 8,
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
    maxLength: password.length <= 100
  };

  const validatePassword = (pwd) => {
    const requirements = {
      minLength: pwd.length >= 8,
      hasUpperCase: /[A-Z]/.test(pwd),
      hasLowerCase: /[a-z]/.test(pwd),
      hasNumber: /[0-9]/.test(pwd),
      hasSpecialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd),
      maxLength: pwd.length <= 100
    };
    
    return Object.values(requirements).every(req => req === true);
  };

  // Resend verification email handler
  const handleResendVerification = async () => {
    if (!registeredEmail || resendCooldown > 0 || resendLoading) {
      return;
    }

    setResendLoading(true);
    try {
      await api.post('/auth/resend-verification', { email: registeredEmail });
      setResendCooldown(30); // Reset cooldown
      showSuccess('Verification email resent! Please check your inbox.');
    } catch (err) {
      // Backend returns success even if email doesn't exist (security)
      setResendCooldown(30); // Reset cooldown
      showSuccess('If an account with that email exists, a verification email has been sent.');
    } finally {
      setResendLoading(false);
    }
  };

  // Countdown timer for resend button
  useEffect(() => {
    if (!verificationSent || resendCooldown <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [verificationSent, resendCooldown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    analyticsEvents.signUpStart();

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!validatePassword(password)) {
      setError('Password does not meet requirements. Please check the requirements below.');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/auth/register', { email, password });
      
      // Check if email verification is required
      if (response.data.requires_verification) {
        setVerificationSent(true);
        setRegisteredEmail(response.data.user.email);
        analyticsEvents.signUpVerificationSent(Boolean(response.data.existing_account));
        
        // Show different message if it's an existing unverified account
        if (response.data.existing_account) {
          showSuccess('A new verification email has been sent! Please check your inbox to verify your account.');
        } else {
          showSuccess('Registration successful! Please check your email to verify your account.');
        }
        
        // Don't log in - user needs to verify email first
        // Clear form
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        return;
      }
      
      // If no verification required (shouldn't happen for new registrations)
      if (response.data.token) {
        setAuth(response.data.token, response.data.user);
        analyticsEvents.signUpSuccess('email');
        showSuccess('Account created successfully!');
        
        // Redirect based on user role
        const user = response.data.user;
        if (user.role === 'admin') {
          navigate('/admin');
        } else if (user.role === 'journalist') {
          navigate('/journalist');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (err) {
      // Extract error message - handle both string and object formats
      let errorMsg = 'Registration failed';
      if (err.response?.data?.error) {
        if (typeof err.response.data.error === 'string') {
          errorMsg = err.response.data.error;
        } else if (err.response.data.error.message) {
          errorMsg = err.response.data.error.message;
        }
      } else if (err.message) {
        errorMsg = err.message;
      }
      setError(errorMsg);
      showError(errorMsg);
      analyticsEvents.signUpFailure('sign_up_failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-shayek-light-50 to-shayek-light-100 dark:from-shayek-dark dark:to-shayek-dark-50 px-4 transition-colors">
      <NotificationContainer 
        notifications={notifications} 
        onRemove={removeNotification} 
      />
      <div className="max-w-md w-full bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek-lg p-8 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
        <h2 className="text-3xl font-bold text-center text-shayek-light-900 dark:text-shayek-dark-900 mb-8">
          AI Moderation Platform
        </h2>
        <h3 className="text-xl font-semibold text-center text-shayek-light-600 dark:text-shayek-dark-600 mb-6">
          Create Account
        </h3>

        {verificationSent ? (
          <div className="mb-4 p-6 bg-green-50 dark:bg-green-900/20 border border-green-400 dark:border-green-800 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-lg font-semibold text-green-800 dark:text-green-300 mb-2">
                  Verification Email Sent!
                </h3>
                <p className="text-sm text-green-700 dark:text-green-400 mb-4">
                  We've sent a verification email to <strong>{registeredEmail}</strong>. Please check your inbox and click the verification link to activate your account.
                </p>
                <p className="text-xs text-green-600 dark:text-green-500 mb-4">
                  Didn't receive the email? Check your spam folder.
                </p>
                {resendCooldown > 0 ? (
                  <p className="text-xs text-green-600 dark:text-green-500 mb-2">
                    Resend verification email available in {resendCooldown} seconds...
                  </p>
                ) : (
                  <button
                    onClick={handleResendVerification}
                    disabled={resendLoading}
                    className="w-full mt-2 px-4 py-2 bg-green-600 dark:bg-green-700 text-white rounded-lg hover:bg-green-700 dark:hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                  >
                    {resendLoading ? 'Sending...' : 'Resend Verification Email'}
                  </button>
                )}
                <Link
                  to="/login"
                  className="inline-block text-sm text-green-700 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 font-medium"
                >
                  Back to Login →
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <>
            {error && (
              <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/20 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-400 rounded">
                {error}
              </div>
            )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
              className="w-full px-4 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg focus:ring-2 focus:ring-shayek-primary focus:border-transparent bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900"
              placeholder="your@email.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                maxLength={100}
                className="w-full px-4 py-2 pr-10 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg focus:ring-2 focus:ring-shayek-primary focus:border-transparent bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-shayek-light-600 dark:text-shayek-dark-600 hover:text-shayek-light-900 dark:hover:text-shayek-dark-900"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.29 3.29m13.42 13.42l-3.29-3.29M3 3l13.42 13.42" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            {password && (
              <div className="mt-2 p-3 bg-shayek-light-50 dark:bg-shayek-dark-50 rounded-lg border border-shayek-light-300 dark:border-shayek-dark-200">
                <p className="text-xs font-semibold text-shayek-light-700 dark:text-shayek-dark-700 mb-2">Password Requirements:</p>
                <ul className="space-y-1 text-xs">
                  <li className={`flex items-center ${passwordRequirements.minLength ? 'text-green-600 dark:text-green-400' : 'text-shayek-light-600 dark:text-shayek-dark-600'}`}>
                    <span className={`mr-2 ${passwordRequirements.minLength ? 'text-green-600 dark:text-green-400' : ''}`}>
                      {passwordRequirements.minLength ? '✓' : '○'}
                    </span>
                    At least 8 characters
                  </li>
                  <li className={`flex items-center ${passwordRequirements.hasUpperCase ? 'text-green-600 dark:text-green-400' : 'text-shayek-light-600 dark:text-shayek-dark-600'}`}>
                    <span className={`mr-2 ${passwordRequirements.hasUpperCase ? 'text-green-600 dark:text-green-400' : ''}`}>
                      {passwordRequirements.hasUpperCase ? '✓' : '○'}
                    </span>
                    One uppercase letter (A-Z)
                  </li>
                  <li className={`flex items-center ${passwordRequirements.hasLowerCase ? 'text-green-600 dark:text-green-400' : 'text-shayek-light-600 dark:text-shayek-dark-600'}`}>
                    <span className={`mr-2 ${passwordRequirements.hasLowerCase ? 'text-green-600 dark:text-green-400' : ''}`}>
                      {passwordRequirements.hasLowerCase ? '✓' : '○'}
                    </span>
                    One lowercase letter (a-z)
                  </li>
                  <li className={`flex items-center ${passwordRequirements.hasNumber ? 'text-green-600 dark:text-green-400' : 'text-shayek-light-600 dark:text-shayek-dark-600'}`}>
                    <span className={`mr-2 ${passwordRequirements.hasNumber ? 'text-green-600 dark:text-green-400' : ''}`}>
                      {passwordRequirements.hasNumber ? '✓' : '○'}
                    </span>
                    One number (0-9)
                  </li>
                  <li className={`flex items-center ${passwordRequirements.hasSpecialChar ? 'text-green-600 dark:text-green-400' : 'text-shayek-light-600 dark:text-shayek-dark-600'}`}>
                    <span className={`mr-2 ${passwordRequirements.hasSpecialChar ? 'text-green-600 dark:text-green-400' : ''}`}>
                      {passwordRequirements.hasSpecialChar ? '✓' : '○'}
                    </span>
                    One special character (!@#$%^&*...)
                  </li>
                </ul>
              </div>
            )}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">
              Confirm Password
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                maxLength={100}
                className="w-full px-4 py-2 pr-10 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg focus:ring-2 focus:ring-shayek-primary focus:border-transparent bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-shayek-light-600 dark:text-shayek-dark-600 hover:text-shayek-light-900 dark:hover:text-shayek-dark-900"
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              >
                {showConfirmPassword ? (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.29 3.29m13.42 13.42l-3.29-3.29M3 3l13.42 13.42" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-shayek-primary dark:bg-shayek-primary-600 text-white py-2 px-4 rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-light focus:outline-none focus:ring-2 focus:ring-shayek-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-shayek"
          >
            {loading ? 'Creating account...' : 'Sign Up'}
          </button>
        </form>

            <p className="mt-6 text-center text-sm text-shayek-light-600 dark:text-shayek-dark-600">
              Already have an account?{' '}
              <Link to="/login" className="text-shayek-primary hover:text-shayek-primary-600 dark:text-shayek-red-light dark:hover:text-shayek-red-light font-medium transition-colors">
                Sign in
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default Register;

